import React, { useState, useRef } from "react";
import { IconButton, Badge, Menu, Box, Typography, Divider, Button } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useNotifications } from "./NotificationContext";
import NotificationItem from "./NotificationItem";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "Constants/RouteUrls";

const NotificationBell: React.FC = () => {
    const { notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications } = useNotifications();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        // Fetch latest notifications on demand to ensure data is perfectly in sync
        fetchNotifications().catch(e => console.error("Error fetching on click", e));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (notification: any) => {
        if (!notification.isRead) {
            await markAsRead(notification.id);
        }
        handleClose();

        // 1. Prioritize Explicit Link from DB
        if (notification.link) {
            // Check for specific Chat URL pattern: /chat/123/messages
            // Support exact string "Chat/123/messages" or "/Chat/123/messages"
            const chatMatch = notification.link.match(/(?:^|\/)chat\/(\d+)\/messages(?:\?(.+))?/i);
            if (chatMatch && chatMatch[1]) {
                const cId = parseInt(chatMatch[1]);
                const queryString = chatMatch[2] ? `&${chatMatch[2]}` : "";
                // Navigate with Query Param for persistence and clearer URL
                navigate(`${RouteUrls.chat}?openCase=${cId}${queryString}`);
                return;
            }

            // For other links (e.g., /order-history), navigate directly
            // Avoid navigating if link looks like an API call
            if (!notification.link.includes("/api/")) {
                navigate(notification.link);
                return;
            }
        }

        // 2. Fallback to Metadata CaseId
        let meta: any = {};
        try {
            meta = notification.metadata ? (typeof notification.metadata === 'string' ? JSON.parse(notification.metadata) : notification.metadata) : {};
        } catch (e) {
            console.error("Error parsing metadata", e);
        }

        const caseId = meta.CaseId || meta.caseId;
        if (caseId) {
            navigate(`${RouteUrls.chat}?openCase=${caseId}`);
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
    };

    return (
        <>
            <IconButton onClick={handleClick} color="inherit">
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: { xs: "90vw", sm: 350 },
                        maxWidth: 500,
                        maxHeight: 400,
                        mt: 1.5,
                        boxShadow: 3,
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#ccc",
                            borderRadius: "4px",
                        },
                    }
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
                    {unreadCount > 0 && (
                        <Button
                            startIcon={<DoneAllIcon fontSize="small" />}
                            onClick={handleMarkAllRead}
                            size="small"
                            sx={{ textTransform: "none", fontSize: "0.75rem" }}
                        >
                            Mark all read
                        </Button>
                    )}
                </Box>
                <Divider />

                {notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.primary">
                            No new notifications
                        </Typography>
                    </Box>
                ) : (
                    (notifications.filter(n => !n.isRead).length > 0 
                        ? notifications.filter(n => !n.isRead) 
                        : notifications
                    ).map((note) => (
                        <NotificationItem
                            key={note.id}
                            notification={note}
                            onClick={handleNotificationClick}
                        />
                    ))
                )}
            </Menu>
        </>
    );
};

export default NotificationBell;
